// src/manager/components/Sidebar.tsx — FINAL COMPLETE VERSION
// All original items retained + polished UI + RTP focus

import { useEffect, useState } from 'react';
import { UserPlus, PlusCircle, DollarSign, Table2, CreditCard, Users, LogOut, Menu, X, Key, Zap, Clock3, SlidersHorizontal, Wallet, ShieldCheck } from 'lucide-react';

interface SidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  onLogout: () => void;
}

export default function Sidebar({ activeSection, onSectionChange, onLogout }: SidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('managerSidebarCollapsed') === 'true');

  useEffect(() => {
    localStorage.setItem('managerSidebarCollapsed', String(isCollapsed));
  }, [isCollapsed]);

  const menuItems = [
    // Core RTP + Table Management (top priority)
    { 
      id: 'create-table', 
      label: 'Create Table', 
      icon: <PlusCircle size={24} />, 
      description: 'Add new table (50-150% RTP)' 
    },
    { 
      id: 'manage-tables',
      label: 'My Tables', 
      icon: <Table2 size={24} />, 
      description: 'View & adjust RTP (50-150%)' 
    },

    // User Management
    { id: 'account', label: 'Account', icon: <Wallet size={24} /> },
    { id: 'register-user', label: 'Register User', icon: <UserPlus size={24} /> },
    { id: 'manage-users', label: 'My Users', icon: <Users size={24} /> },
    { id: 'distribute-credits', label: 'Distribute Credits', icon: <CreditCard size={24} /> },

    // Cashout & Rate
    { id: 'set-cashout-rate', label: 'Set Cashout Rate', icon: <DollarSign size={24} /> },
    { id: 'cashouts', label: 'Cashout Requests', icon: <CreditCard size={24} /> },

    // RTP controls
    { id: 'global-rtp', label: 'Global RTP', icon: <Zap size={24} />, description: 'Auto-table RTP control' },
    { id: 'risk-control', label: 'Risk Control', icon: <ShieldCheck size={24} />, description: 'Protected reward reserve' },
    { id: 'volatility-control', label: 'Volatility Control', icon: <SlidersHorizontal size={24} />, description: 'Tune win profile per table' },
    { id: 'time-rtp-schedule', label: 'Time RTP Schedule', icon: <Clock3 size={24} />, description: 'Set time-based RTP slots' },

    // Settings
    { id: 'change-password', label: 'Change Password', icon: <Key size={24} /> },
  ];

  return (
    <div className={`bg-gradient-to-b from-purple-900 to-purple-800 text-white h-full ${isCollapsed ? 'w-16' : 'w-72'} transition-all duration-300 flex flex-col border-r border-purple-700 shadow-xl`}>
      {/* Header */}
      <div className="p-4 flex justify-between items-center border-b border-purple-700">
        {!isCollapsed && <h2 className="text-xl font-bold tracking-tight">Manager Panel</h2>}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="bf-icon-gloss p-2 rounded-lg transition focus:outline-none focus:ring-2 focus:ring-purple-400"
          aria-label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
        >
          {isCollapsed ? <Menu size={24} /> : <X size={24} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2">
        {menuItems.map(item => (
          <button
            key={item.id}
            className={`group flex items-center w-full p-3.5 text-left transition-all duration-200 ${
              activeSection === item.id
                ? 'bg-purple-700/80 border-l-4 border-purple-400 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.16)]'
                : 'hover:bg-purple-800/50 text-purple-100 hover:text-white'
            }`}
            onClick={() => {
              onSectionChange(item.id);
            }}
            aria-label={item.label}
          >
            <span className={`${isCollapsed ? 'mx-auto' : 'mr-3.5'} text-purple-300 group-hover:text-purple-200 transition-colors`}>
              {item.icon}
            </span>

            {!isCollapsed && (
              <div className="flex flex-col">
                <span className="font-medium">{item.label}</span>
                {item.description && (
                  <span className="text-xs text-purple-300/80 group-hover:text-purple-200/90 transition-opacity">
                    {item.description}
                  </span>
                )}
              </div>
            )}
          </button>
        ))}
      </nav>

      {/* Logout */}
      <button
        className="bf-glossy-danger flex items-center w-full p-4 text-left transition-all duration-200 border-t border-purple-700 mt-auto"
        onClick={onLogout}
        aria-label="Logout"
      >
        <span className={`${isCollapsed ? 'mx-auto' : 'mr-3.5'} text-red-300`}>
          <LogOut size={24} />
        </span>
        {!isCollapsed && <span className="font-medium text-red-200">Logout</span>}
      </button>

      {/* Footer Hint */}
      {!isCollapsed && (
        <div className="p-4 text-xs text-purple-300/80 border-t border-purple-700 bg-purple-950/30">
          RTP Control: <span className="font-semibold text-purple-200">50-150%</span> - Manager Level
        </div>
      )}
    </div>
  );
}
